import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getDemoDashboard } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/toast';
import { formatPrice } from '../utils/formatPrice';
import PriceTag from '../components/ui/PriceTag';
import './ManagerDashboard.css';
import './Manager/AnalyticsDashboard.css';
import './DemoManagerDashboard.css';

// Warm, colorblind-aware chart palette (shared with the real analytics dashboard).
const COLORS = ['#ea580c', '#0d9488', '#a16207', '#dc2626', '#7c3aed', '#db2777'];

const STATUS_COLOR = {
  pending: 'warning',
  preparing: 'info',
  ready: 'success',
  completed: 'success',
  cancelled: 'error',
};

const DemoManagerDashboard = () => {
  const { t, i18n } = useTranslation();
  const { restaurantId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginAsDemo, isAuthenticated, logout } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [demoStarting, setDemoStarting] = useState(false);

  const handleTryAsManager = async () => {
    if (demoStarting) return;
    setDemoStarting(true);
    const toastId = toast.loading(t('demoDashboard.cta.demoStartingToast'));
    try {
      if (isAuthenticated()) {
        await logout();
      }
      const result = await loginAsDemo();
      if (result.success) {
        toast.success(t('demoDashboard.cta.demoStartedToast'), { id: toastId });
        navigate('/manager/dashboard');
      } else {
        toast.error(result.error, { id: toastId });
      }
    } catch (err) {
      toast.error(err.message || 'Could not start demo session.', { id: toastId });
    } finally {
      setDemoStarting(false);
    }
  };

  const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'es' ? 'es-ES' : 'en-US';

  // The order the prospect just placed (passed via router state, or ?order= for shareable links).
  const highlightOrderId = location.state?.orderId ?? searchParams.get('order') ?? null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getDemoDashboard(restaurantId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  const currency = data?.restaurant?.currency || null;
  const formatCurrency = (value) => formatPrice(value, currency);

  // Inline relative-time helper — reuses the existing dashboard.manager.* keys.
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return t('dashboard.manager.justNow');
    if (mins === 1) return t('dashboard.manager.oneMinAgo');
    if (mins < 60) return t('dashboard.manager.minsAgo', { count: mins });
    if (hours === 1) return t('dashboard.manager.oneHourAgo');
    if (hours < 24) return t('dashboard.manager.hoursAgo', { count: hours });
    if (days === 1) return t('dashboard.manager.oneDayAgo');
    return t('dashboard.manager.daysAgo', { count: days });
  };

  const salesTrendFormatted = useMemo(
    () =>
      (data?.salesTrend || []).map((d) => ({
        date: typeof d.date === 'string' ? d.date.slice(0, 10) : d.date,
        revenue: parseFloat(d.revenue) || 0,
        order_count: Number(d.order_count) || 0,
      })),
    [data]
  );

  const topItemsFormatted = useMemo(
    () =>
      (data?.topItems || []).map((it) => ({
        ...it,
        total_revenue: parseFloat(it.total_revenue) || 0,
      })),
    [data]
  );

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="spinner" />
        <p>{t('demoDashboard.loading')}</p>
      </div>
    );
  }

  const ctaBlock = (
    <div className="demo-dash-cta">
      <h2>{t('demoDashboard.cta.title')}</h2>
      <p>{t('demoDashboard.cta.tryAsManagerHint')}</p>
      <div className="demo-dash-cta-actions">
        <button
          type="button"
          className="mgr-btn mgr-btn-primary mgr-btn-lg"
          onClick={handleTryAsManager}
          disabled={demoStarting}
          aria-busy={demoStarting}
        >
          {t('demoDashboard.cta.tryAsManager')}
        </button>
        <Link to="/register" className="mgr-btn mgr-btn-ghost mgr-btn-lg">
          {t('demoDashboard.cta.startTrial')}
        </Link>
        <Link to={`/menu/${restaurantId}?mode=demo`} className="mgr-btn mgr-btn-ghost mgr-btn-lg">
          {t('demoDashboard.cta.backToMenu')}
        </Link>
      </div>
    </div>
  );

  // ── Error / empty ────────────────────────────────────────────────────────
  const isEmpty = !data || !data.overview || data.overview.totalOrders === 0;
  if (error || isEmpty) {
    return (
      <div className="mgr-dashboard fade-in">
        <div className="demo-dash-banner" role="status">
          <span className="demo-dash-banner-icon" aria-hidden="true">✨</span>
          <div className="demo-dash-banner-body">
            <strong>{t('demoDashboard.banner.title')}</strong>
            <p>{t('demoDashboard.banner.text')}</p>
          </div>
        </div>
        <div className="mgr-empty demo-dash-empty">
          <div className="mgr-empty-icon" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
            </svg>
          </div>
          <h3>{t('demoDashboard.empty.title')}</h3>
          <p>{t('demoDashboard.empty.text')}</p>
        </div>
        {ctaBlock}
      </div>
    );
  }

  const { restaurant, overview, recentOrders, peakHours } = data;

  return (
    <div className="mgr-dashboard fade-in">
      {/* ── Demo preview banner ──────────────────────────────────── */}
      <div className="demo-dash-banner" role="status">
        <span className="demo-dash-banner-icon" aria-hidden="true">✨</span>
        <div className="demo-dash-banner-body">
          <strong>{t('demoDashboard.banner.title')}</strong>
          <p>{t('demoDashboard.banner.text')}</p>
        </div>
      </div>

      {/* ── Greeting header ──────────────────────────────────────── */}
      <div className="mgr-greeting">
        <div>
          <h1 className="mgr-greeting-title">{t('demoDashboard.title')}</h1>
          <p className="mgr-greeting-sub">{t('demoDashboard.subtitle')}</p>
        </div>
        <div className="mgr-restaurant-badge">
          <span className="mgr-restaurant-dot" />
          {restaurant?.name}
        </div>
      </div>

      {/* ── KPI cards ────────────────────────────────────────────── */}
      <div className="kpi-grid">
        <div className="kpi-card revenue">
          <div className="kpi-header">
            <span className="kpi-icon" aria-hidden="true">💰</span>
            <span className="kpi-label">{t('demoDashboard.kpi.totalRevenue')}</span>
          </div>
          <div className="kpi-value">
            <PriceTag amount={overview.totalRevenue} currency={currency} size="lg" color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
          </div>
        </div>

        <div className="kpi-card orders">
          <div className="kpi-header">
            <span className="kpi-icon" aria-hidden="true">📦</span>
            <span className="kpi-label">{t('demoDashboard.kpi.totalOrders')}</span>
          </div>
          <div className="kpi-value">{overview.totalOrders}</div>
          <div className="kpi-subtext">
            {t('demoDashboard.kpi.completedShort', { count: overview.completedOrders })}
          </div>
        </div>

        <div className="kpi-card avg-order">
          <div className="kpi-header">
            <span className="kpi-icon" aria-hidden="true">🧾</span>
            <span className="kpi-label">{t('demoDashboard.kpi.avgOrderValue')}</span>
          </div>
          <div className="kpi-value">
            <PriceTag amount={overview.avgOrderValue} currency={currency} size="lg" color="inherit" weight="bold" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
          </div>
          <div className="kpi-subtext">
            {t('demoDashboard.kpi.tablesShort', { count: overview.uniqueTables })}
          </div>
        </div>

        <div className="kpi-card prep-time">
          <div className="kpi-header">
            <span className="kpi-icon" aria-hidden="true">⏱️</span>
            <span className="kpi-label">{t('demoDashboard.kpi.avgPrepTime')}</span>
          </div>
          <div className="kpi-value">
            {overview.avgPrepTime} {t('demoDashboard.kpi.minutesShort')}
          </div>
          {overview.maxPrepTime > 0 && (
            <div className="kpi-subtext">
              {t('demoDashboard.kpi.prepRange', { min: overview.minPrepTime, max: overview.maxPrepTime })}
            </div>
          )}
        </div>
      </div>

      {/* ── Charts ───────────────────────────────────────────────── */}
      <div className="charts-grid">
        <div className="chart-card full-width">
          <div className="chart-header">
            <h3>{t('demoDashboard.charts.salesTrend')}</h3>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesTrendFormatted}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [name === t('demoDashboard.charts.orders') ? value : formatCurrency(value), name]}
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke={COLORS[0]} strokeWidth={3} name={t('demoDashboard.charts.revenue')} dot={{ fill: COLORS[0], r: 4 }} />
                <Line yAxisId="right" type="monotone" dataKey="order_count" stroke={COLORS[1]} strokeWidth={2} name={t('demoDashboard.charts.orders')} dot={{ fill: COLORS[1], r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>{t('demoDashboard.charts.topItems')}</h3>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topItemsFormatted} layout="vertical" margin={{ left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} width={110} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Bar dataKey="total_revenue" fill={COLORS[0]} radius={[0, 8, 8, 0]} name={t('demoDashboard.charts.revenue')} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>{t('demoDashboard.charts.peakHours')}</h3>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={peakHours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={(hour) => `${hour}:00`} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  labelFormatter={(hour) => `${hour}:00 – ${Number(hour) + 1}:00`}
                />
                <Bar dataKey="orderCount" fill={COLORS[2]} radius={[8, 8, 0, 0]} name={t('demoDashboard.charts.orders')} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── Recent orders (read-only) ────────────────────────────── */}
      <div className="mgr-card">
        <div className="mgr-card-header">
          <h2>{t('demoDashboard.recentOrders.title')}</h2>
        </div>
        {recentOrders.length === 0 ? (
          <div className="mgr-empty">
            <p>{t('demoDashboard.recentOrders.empty')}</p>
          </div>
        ) : (
          <div className="mgr-orders-table">
            <div className="mgr-orders-head">
              <span>{t('demoDashboard.recentOrders.table')}</span>
              <span>{t('demoDashboard.recentOrders.status')}</span>
              <span>{t('demoDashboard.recentOrders.time')}</span>
              <span className="mgr-text-right">{t('demoDashboard.recentOrders.amount')}</span>
            </div>
            {recentOrders.map((order) => {
              const isYours = highlightOrderId != null && String(order.id) === String(highlightOrderId);
              return (
                <div
                  key={order.id}
                  className={`mgr-order-row ${isYours ? 'mgr-order-row--highlight' : ''}`}
                >
                  <span className="mgr-order-table">
                    {order.order_type === 'car' ? `🚗 ${t('orders.carOrder')}` : order.table_number}
                    {isYours && (
                      <span className="demo-dash-your-order">{t('demoDashboard.recentOrders.yourOrder')}</span>
                    )}
                  </span>
                  <span className="mgr-order-status-cell">
                    <span className={`mgr-status-pill mgr-status-${STATUS_COLOR[order.status] || 'default'}`}>
                      {t(`orders.status.${order.status}`, { defaultValue: order.status })}
                    </span>
                  </span>
                  <span className="mgr-order-time">{formatTimeAgo(order.created_at)}</span>
                  <span className="mgr-order-amount">
                    <PriceTag amount={order.total_amount} currency={currency} locale={locale} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      {ctaBlock}
    </div>
  );
};

export default DemoManagerDashboard;
